import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPanelTitle,
  AdminStatusBadge,
  AdminTableBody,
  AdminSupportingText,
  AdminAlertText,
  AdminRowAction,
  AdminHeaderAction,
  AdminLinkAction,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminTabButton,
  AdminFlagStatusBadge,
  AdminProfileAvatar,
  AdminDetailTableHeader,
  AdminFormSecondaryAction,
  ADMIN_DETAIL_MAIN_HEADER_CLASS,
  ADMIN_TAB_CONTENT_CONTAINER_CLASS,
  ADMIN_TAB_CONTENT_OFFSET_CLASS,
  ADMIN_DETAIL_TABLE_CLASS,
} from '../shared/adminUx';

import Profile from '../../../assets/img/User.png';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import { ACTION_POPUP_CONTENT_WIDTH_CLASS } from '../../../components/popup/popupLayoutSpec';
import useClickOutside from '../../../Hooks/useClickOutside';
import { CONFIG_LIST_PAGE_SIZE } from '../../../utils/configurationTableUx';
import RatingStars from '../../../components/common/RatingStars/RatingStars';
import { getCustomersForProfileScenarios, getCustomerScenarioByCustomerId, CUSTOMER_ACCOUNT_FLAG_SAMPLES } from '../../../data/profileModerationScenarios';
import { getCustomersFromReviewScenarios } from '../../../data/reviewModerationScenarios';
import {
  getAccountAuditLogRows,
  recordAccountActivity,
  syncLocalAuditLogsToChangeHistory,
} from '../../../data/changeHistoryAudit';

const mapCentralAuditToLocal = (rows) =>
  (rows || []).map((row) => ({
    id: row.changeId || row.id,
    timestamp: row.dateTime || row.timestamp,
    performedBy: row.performedBy,
    action: row.event || row.action,
    summary: row.summary,
    details: row.details,
    relatedRecord: row.relatedRecord || row.record || '—',
  }));

const refreshCustomerAuditLogs = (accountRef, fallbackLogs = []) => {
  if (!accountRef) return fallbackLogs;
  syncLocalAuditLogsToChangeHistory({
    logs: fallbackLogs,
    module: 'Customers',
    sourceModuleRef: 'customers',
    sourceItemRef: accountRef,
    accountRef,
    changedItem: accountRef,
    itemId: accountRef,
  });
  return mapCentralAuditToLocal(getAccountAuditLogRows(accountRef));
};

const CustomerSecurityConfirmModal = ({ title, message, confirmLabel, onConfirm, onClose }) => (
  <div
    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4"
    onClick={onClose}
    role="presentation"
  >
    <div
      className={`bg-white rounded-xl shadow-lg ${ACTION_POPUP_CONTENT_WIDTH_CLASS}`}
      onClick={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
    >
      <div className="px-5 pt-5 pb-3 border-b border-lightgray">
        <AdminPanelTitle className="text-offblack text-center">{title}</AdminPanelTitle>
      </div>
      <div className="px-5 py-4">
        <AdminSupportingText className="text-offblack text-center">{message}</AdminSupportingText>
      </div>
      <div className="px-5 pb-5 flex flex-wrap justify-end gap-3">
        <AdminFormSecondaryAction type="button" onClick={onClose}>
          Cancel
        </AdminFormSecondaryAction>
        <AdminHeaderAction type="button" onClick={onConfirm}>
          {confirmLabel}
        </AdminHeaderAction>
      </div>
    </div>
  </div>
);

/** Flag ID | Case ID | Source | Reason | Severity | Case Status | Created On | Last Updated | Action */
const FLAGS_TABLE_GRID =
  'minmax(0, 0.7fr) minmax(0, 0.9fr) minmax(0, 0.85fr) minmax(0, 1.2fr) minmax(0, 0.5fr) minmax(0, 0.85fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)';

/** Date and Time | Event | Details | Related Record */
const AUDIT_LOG_TABLE_GRID =
  'minmax(0, 1.1fr) minmax(0, 1.2fr) minmax(0, 2fr) minmax(0, 0.9fr)';

const CustomerDetail = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('overview');
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customerIdCopied, setCustomerIdCopied] = useState(false);
  const [copiedContactField, setCopiedContactField] = useState(null);
  const [securityConfirm, setSecurityConfirm] = useState(null);
  
  // Flags filter refs
  const flagsSourceFilterRef = useRef(null);
  const flagsCategoryFilterRef = useRef(null);
  const flagsSeverityFilterRef = useRef(null);
  const flagsStatusFilterRef = useRef(null);
  const flagsDateRangeFilterRef = useRef(null);
  
  // Click outside handlers
  useClickOutside(flagsSourceFilterRef, () => setFlagsSourceFilterOpen(false));
  useClickOutside(flagsCategoryFilterRef, () => setFlagsCategoryFilterOpen(false));
  useClickOutside(flagsSeverityFilterRef, () => setFlagsSeverityFilterOpen(false));
  useClickOutside(flagsStatusFilterRef, () => setFlagsStatusFilterOpen(false));
  useClickOutside(flagsDateRangeFilterRef, () => setFlagsDateRangeFilterOpen(false));

  const getRefLink = (refId) => {
    if (!refId) return null;
    if (refId.startsWith('TKT-')) return `/admin/support/${refId}`;
    if (refId.startsWith('RV-')) return `/admin/reviews/${refId}`;
    if (refId.startsWith('QR-')) return `/admin/quotes/${refId}`;
    if (refId.startsWith('MSG-')) return `/admin/messages/${refId}`;
    return null;
  };

  // Pagination states for Quotes, Messages & Activity
  const [quotesCurrentPage, setQuotesCurrentPage] = useState(1);
  const [messagesCurrentPage, setMessagesCurrentPage] = useState(1);
  const [activityCurrentPage, setActivityCurrentPage] = useState(1);
  const itemsPerPage = 6;

  // Messages & Quotes filters
  const [messagesFilters, setMessagesFilters] = useState({
    type: 'All', // All, Message, Direct Quote, Group Quote
    status: 'All', // All, New Quote, Quote Replied, etc.
    business: 'All',
    dateFrom: '',
    dateTo: '',
    flagged: 'All' // All, Yes, No
  });
  
  // Messages & Quotes filter dropdown states
  const [typeFilterOpen, setTypeFilterOpen] = useState(false);
  const [statusFilterOpen, setStatusFilterOpen] = useState(false);
  const [businessFilterOpen, setBusinessFilterOpen] = useState(false);
  const [dateRangeFilterOpen, setDateRangeFilterOpen] = useState(false);
  const [flaggedFilterOpen, setFlaggedFilterOpen] = useState(false);
  
  // Row actions for messages/quotes table
  const [rowActionsOpen, setRowActionsOpen] = useState({});
  const rowActionsRefs = useRef({});

  // Flags tab filters
  const [flagsFilters, setFlagsFilters] = useState({
    source: 'All',
    category: 'All',
    severity: 'All',
    status: 'All',
    dateRange: 'All'
  });

  // Flags filter dropdown states
  const [flagsSourceFilterOpen, setFlagsSourceFilterOpen] = useState(false);
  const [flagsCategoryFilterOpen, setFlagsCategoryFilterOpen] = useState(false);
  const [flagsSeverityFilterOpen, setFlagsSeverityFilterOpen] = useState(false);
  const [flagsStatusFilterOpen, setFlagsStatusFilterOpen] = useState(false);
  const [flagsDateRangeFilterOpen, setFlagsDateRangeFilterOpen] = useState(false);
  
  // Flags table pagination (same as Customers list)
  const [flagsCurrentPage, setFlagsCurrentPage] = useState(1);
  useEffect(() => {
    setFlagsCurrentPage(1);
  }, [customerId]);

  // Refs for messages filters
  const typeFilterRef = useRef(null);
  const statusFilterRef = useRef(null);
  const businessFilterRef = useRef(null);
  const dateRangeFilterRef = useRef(null);
  const flaggedFilterRef = useRef(null);
  
  // Click outside handlers for messages filters
  useClickOutside(typeFilterRef, () => setTypeFilterOpen(false));
  useClickOutside(statusFilterRef, () => setStatusFilterOpen(false));
  useClickOutside(businessFilterRef, () => setBusinessFilterOpen(false));
  useClickOutside(dateRangeFilterRef, () => setDateRangeFilterOpen(false));
  useClickOutside(flaggedFilterRef, () => setFlaggedFilterOpen(false));
  
  // Toggle row actions for messages/quotes
  const toggleRowActions = (itemId) => {
    setRowActionsOpen((prev) => {
      if (prev[itemId]) return {};
      return { [itemId]: true };
    });
  };
  
  // Handle row action clicks for messages/quotes
  const handleRowAction = (action, item) => {
    setRowActionsOpen(prev => ({ ...prev, [item.id]: false }));
    
    switch (action) {
      case 'openThread':
        if (item.itemType === 'message') {
          navigate(`/admin/communication/all/${item.id}`);
        }
        break;
      case 'openQuote':
        if (item.itemType === 'directQuote') {
          navigate(`/admin/quotes/all/${item.id || item.quoteId}`);
        } else if (item.itemType === 'groupQuote') {
          navigate(`/admin/quotes/group/${item.id || item.quoteId}`);
        }
        break;
      case 'viewBusiness':
        if (item.businessId) {
          navigate(`/admin/users/businesses/${item.businessId}`);
        }
        break;
      default:
        break;
    }
  };
  
  // Click outside handler for row actions
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideAll = Object.values(rowActionsRefs.current).every(ref => {
        return ref && !ref.contains(event.target);
      });
      
      if (isOutsideAll) {
        setRowActionsOpen({});
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Row actions for reviews table
  const [reviewsRowActionsOpen, setReviewsRowActionsOpen] = useState({});
  const reviewsRowActionsRefs = useRef({});
  
  // Toggle row actions for reviews
  const toggleReviewsRowActions = (reviewId) => {
    setReviewsRowActionsOpen((prev) => {
      if (prev[reviewId]) return {};
      return { [reviewId]: true };
    });
  };
  
  // Handle row action clicks for reviews
  const handleReviewsRowAction = (action, review) => {
    setReviewsRowActionsOpen(prev => ({ ...prev, [review.id]: false }));
    
    switch (action) {
      case 'openReview':
        navigate(`/admin/reviews/${review.id}`);
        break;
      case 'openBusiness':
        if (review.businessId) {
          navigate(`/admin/users/businesses/${review.businessId}`);
        }
        break;
      case 'openModeration':
        navigate(`/admin/moderation/queue?reviewId=${review.id}`);
        break;
      default:
        break;
    }
  };
  
  // Click outside handler for reviews row actions
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideAll = Object.values(reviewsRowActionsRefs.current).every(ref => {
        return ref && !ref.contains(event.target);
      });
      
      if (isOutsideAll) {
        setReviewsRowActionsOpen({});
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Generate customer detail data based on customerId
  useEffect(() => {
    const fetchCustomerDetail = async () => {
      setLoading(true);
      try {
        // TODO: Replace with actual API call
        await new Promise(resolve => setTimeout(resolve, 500));
        
        const passedCustomer = location.state?.customer;
        let foundCustomer = passedCustomer;
        if (!foundCustomer && customerId) {
          const scenarioCustomers = [...getCustomersForProfileScenarios(), ...getCustomersFromReviewScenarios()];
          const normalized = String(customerId).replace(/^CUS-?/i, '');
          foundCustomer = scenarioCustomers.find((c) => String(c.customerId || c.id).replace(/^CUS-?/i, '') === normalized) || null;
        }
        const isScenarioCustomer = foundCustomer?.scenarioKey != null || (customerId && getCustomerScenarioByCustomerId(customerId));

        if (foundCustomer) {
          
          // Merge with existing detail data structure
          const customerData = {
            ...foundCustomer,
            customerScore: foundCustomer.accountScore ?? foundCustomer.customerScore ?? 100,
            accountScore: foundCustomer.accountScore ?? foundCustomer.customerScore ?? 100,
            strikeRate: 0,
            reviewsGiven: foundCustomer.reviewsGiven ?? foundCustomer.reviewsCount ?? 0,
            quotesReceived: foundCustomer.quotesCount || 0,
            messageThreads: foundCustomer.messageThreads ?? foundCustomer.messagesCount ?? 0,
            status: foundCustomer.status || foundCustomer.accountStatus || 'Active',
            verificationStatus:
              foundCustomer.verificationStatus ||
              (foundCustomer.emailVerified ? 'Verified' : 'Pending'),
            suspended:
              foundCustomer.status === 'Suspended' ||
              foundCustomer.status === 'Banned' ||
              foundCustomer.status === 'Restricted',
            emailVerifiedDate: '12 Aug 2025',
            phoneVerifiedDate: foundCustomer.phoneVerified ? '12 Aug 2025' : null,
            emailVerifyCompleted: '12 Aug 2025',
            phoneVerifyCompleted: foundCustomer.phoneVerified ? '12 Aug 2025' : null,
            ausPostIdStatus: foundCustomer.ausPostIdStatus || 'not started',
            supportTickets: foundCustomer.supportTickets ?? foundCustomer.openSupportTickets ?? 2,
            openSupportTickets: foundCustomer.openSupportTickets ?? foundCustomer.supportTickets ?? 2,
            activeAppeals: foundCustomer.activeAppeals ?? 1,
            
            // Activity stats (30 days)
            activeSessions: foundCustomer.currentActiveSessions ?? foundCustomer.activeSessions ?? 2,
            messagesStarted: foundCustomer.messagesCount ?? foundCustomer.messageThreads ?? 0,
            quoteRequestsCount: foundCustomer.quotesCount ?? foundCustomer.directQuotes ?? 0,
            reviewsPosted: foundCustomer.reviewsGiven ?? foundCustomer.reviewsCount ?? 0,
            retention30Days: true,
            avgSessionsPerWeek: 2.3,
            
            // Sessions & Usage
            sessions7d: 3,
            sessions30d: 9,
            sessions90d: 22,
            avgSessionDuration: '6m 14s',
            failedLogins: 3,
            passwordLastChanged: '2025-08-15T10:30:00Z',
            
            // Engagement (7d | 30d | 90d)
            messages7d: 1,
            messages30d: 3,
            messages90d: 8,
            quotes7d: 1,
            quotes30d: 2,
            quotes90d: 6,
            reviews7d: 0,
            reviews30d: 1,
            reviews90d: 3,
            
            // Cohort
            signupMonth: 'Aug 2025',
            retained30to60d: true,
            
            // Reviews
            totalReviews: 92,
            averageRating: 4.8,
            ratingBreakdown: {
              five: { count: 2200, percentage: 75 },
              four: { count: 550, percentage: 20 },
              three: { count: 550, percentage: 5 }
            },
            aiReviewAnalysis: {
              summary: 'The customer was satisfied with the product, noting its quality, durability, and quick, helpful support.',
              sentiment: 'positive'
            },
            
            // Security
            mfaEnabled: false,
            lastIP: '203.45.118.92',
            lastIPCountry: 'AU',
            lastLogin: foundCustomer.lastLogin || foundCustomer.lastActive || '2026-08-05T09:10:00.000Z',
            lastLocation: 'Melbourne, VIC',
            failedLoginAttempts24h: foundCustomer.failedLoginAttempts24h ?? 1,
            failedLoginAttempts7Days: foundCustomer.failedLoginAttempts7Days ?? 3,
            trustedDevices: foundCustomer.trustedDevices ?? 2,
            currentActiveSessions: foundCustomer.currentActiveSessions ?? foundCustomer.activeSessions ?? 2,
            lastDeviceUsed: foundCustomer.lastDeviceUsed || 'iPhone 16 · Safari',
            lastDeviceAdded: foundCustomer.lastDeviceAdded || '2026-07-28',
            devices: [
              { device: 'Safari — macOS', date: '23 Oct 2025 14:22', location: 'Melbourne, AU' },
              { device: 'Chrome — Android 14', date: '18 Oct 2025 21:10', location: 'Melbourne, AU' },
              { device: 'Chrome — Windows 11', date: '15 Oct 2025 09:30', location: 'Melbourne, AU' }
            ],
            accountRestrictions: [
              {
                type: 'Login Blocked',
                reason: 'Security Hold',
                admin: 'Mark Johnson',
                applied: '02 Nov 2025 04:00 pm'
              },
              {
                type: 'Messaging Blocked',
                reason: 'Abuse Complaint',
                admin: 'Alex Roberts',
                applied: '05 Nov 2025 11:18 am'
              }
            ],
            totalSessions: 22,
            avgSessionDuration: '6m 14s',
            failedLoginAttempts: 3,
            passwordResetLogs: [
              { date: '02 Nov 2025 02:15 pm', type: 'Admin-Forced' },
              { date: '14 Aug 2025 09:30 am', type: 'User-Initiated' }
            ],
            emailVerified: true,
            emailVerifiedDate: '12 Aug 2025 08:16',
            phoneVerified: true,
            phoneVerifiedDate: '12 Aug 2025 08:21',
            twoFAStatus: 'Disabled',
            twoFALastUpdated: '05 Sep 2025',
            
            // Recent items for overview
            recentMessages: [
              { business: 'Northside Plumbing', date: '23 Oct 2025', status: 'Open' },
              { business: 'Bolt Electrical', date: '10 Oct 2025', status: 'Closed' },
              { business: 'Coastal Gas', date: '05 Oct 2025', status: 'Open' }
            ],
            recentQuotes: [
              { business: 'Northside Plumbing', date: '20 Oct 2025', status: 'Replied' },
              { business: 'Bolt Electrical', date: '10 Oct 2025', status: 'Sent' },
              { business: 'Rapid Roofers', date: '02 Oct 2025', status: 'Replied' }
            ],
            latestReviews: [
              { business: 'Northside Plumbing', rating: 5, date: '21 Oct 2025' },
              { business: 'Bolt Electrical', rating: 4, date: '04 Oct 2025' },
              { business: 'Coastal Gas', rating: 5, date: '14 Sep 2025' }
            ],
            
            // Reviews data
            reviews: [
              { 
                id: 'RV-1',
                orderId: 'Order-12567',
                date: '21 Oct 2025',
                postedDate: 'Oct 21, 2025',
                business: 'Northside Plumbing',
                businessId: 'PL-3920',
                businessProfilePicture: `https://i.pravatar.cc/150?img=1`,
                rating: 5,
                title: 'Great service',
                status: 'Published',
                fullText: 'I recently needed plumbing work done and Northside Plumbing was fantastic. The plumber arrived on time, was professional, and completed the work efficiently. Highly recommended!',
                shortText: 'I recently needed plumbing work done...',
                item: 'Plumbing Service',
                tags: ['Mixer repair', 'Kitchen plumbing', 'Emergency service'],
                images: [
                  'https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=200&h=200&fit=crop',
                  'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=200&h=200&fit=crop',
                  'https://images.unsplash.com/photo-1607472586893-edb57bdc0e39?w=200&h=200&fit=crop'
                ],
                videos: [],
                moderationNotes: '',
                readMore: true
              },
              { 
                id: 'RV-2',
                orderId: 'Order-12901',
                date: '10 Oct 2025',
                postedDate: 'Oct 10, 2025',
                business: 'Bolt Electrical',
                businessId: 'EL-1187',
                businessProfilePicture: `https://i.pravatar.cc/150?img=2`,
                rating: 4,
                title: 'Quick & tidy',
                status: 'Published',
                fullText: 'They fixed my electrical issue quickly and left the place clean. Good service overall. The technician was knowledgeable and explained everything clearly.',
                shortText: 'They fixed my electrical issue quickly...',
                item: 'Electrical Service',
                tags: ['Circuit repair', 'Panel upgrade', 'Safety inspection'],
                images: [
                  'https://images.unsplash.com/photo-1621905251918-48416bd8575a?w=200&h=200&fit=crop'
                ],
                videos: [],
                moderationNotes: '',
                readMore: true
              },
              { 
                id: 'RV-3',
                orderId: 'Order-12045',
                date: '02 Oct 2025',
                postedDate: 'Oct 2, 2025',
                business: 'Rapid Roofers',
                businessId: 'RO-9401',
                businessProfilePicture: `https://i.pravatar.cc/150?img=3`,
                rating: 3,
                title: 'Average service',
                status: 'Hidden',
                fullText: 'They arrived on time but the work quality was average.',
                shortText: 'They arrived on time but...',
                item: 'Roof Tile Replacement',
                tags: ['Roof repair', 'Tile replacement', 'Gutter cleaning'],
                images: [
                  'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=200&h=200&fit=crop',
                  'https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=200&h=200&fit=crop'
                ],
                videos: [],
                moderationNotes: '',
                readMore: false
              },
              { 
                id: 'RV-4',
                orderId: 'Order-11580',
                date: '14 Sep 2025',
                postedDate: 'Sep 14, 2025',
                business: 'Coastal Gas',
                businessId: 'GA-2203',
                businessProfilePicture: `https://i.pravatar.cc/150?img=4`,
                rating: 5,
                title: 'Highly recommended',
                status: 'Published',
                fullText: 'Excellent service from start to finish. The technician was knowledgeable and friendly. They fixed my heating issue quickly and explained everything clearly.',
                shortText: 'Excellent service from start to finish...',
                item: 'Gas Heating System Repair',
                tags: ['Heating repair', 'Gas service', 'System maintenance'],
                images: [
                  'https://images.unsplash.com/photo-1621905252507-b35492cc74b4?w=200&h=200&fit=crop',
                  'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=200&h=200&fit=crop'
                ],
                videos: [],
                moderationNotes: '',
                readMore: false
              },
              { 
                id: 'RV-5',
                orderId: 'Order-11200',
                date: '28 Aug 2025',
                postedDate: 'Aug 28, 2025',
                business: 'Ace Carpentry',
                businessId: 'CA-5501',
                businessProfilePicture: `https://i.pravatar.cc/150?img=5`,
                rating: 4,
                title: 'Good work',
                status: 'Removed',
                fullText: 'Professional service and good quality work.',
                shortText: 'Professional service...',
                item: 'Carpentry Service',
                tags: ['Cabinet installation', 'Custom shelving', 'Door repair'],
                images: [
                  'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=200&h=200&fit=crop',
                  'https://images.unsplash.com/photo-1556911220-bff31c812dba?w=200&h=200&fit=crop'
                ],
                videos: [],
                moderationNotes: '',
                readMore: false
              },
              { 
                id: 'RV-6',
                orderId: 'Order-11050',
                date: '25 Aug 2025',
                postedDate: 'Aug 25, 2025',
                business: 'Modern Painters',
                businessId: 'PA-6602',
                businessProfilePicture: `https://i.pravatar.cc/150?img=6`,
                rating: 5,
                title: 'Excellent painting job',
                status: 'Published',
                fullText: 'The painting work was done to a very high standard. Clean, professional, and finished on time.',
                shortText: 'The painting work was done...',
                item: 'Interior Painting',
                tags: ['Interior painting', 'Wall preparation', 'Color consultation'],
                images: [
                  'https://images.unsplash.com/photo-1589939705384-5185137a7f0f?w=200&h=200&fit=crop'
                ],
                videos: [],
                moderationNotes: '',
                readMore: false
              },
              { 
                id: 'RV-7',
                orderId: 'Order-10980',
                date: '22 Aug 2025',
                postedDate: 'Aug 22, 2025',
                business: 'Green Lawn Care',
                businessId: 'LC-8801',
                businessProfilePicture: `https://i.pravatar.cc/150?img=7`,
                rating: 2,
                title: 'Disappointing service',
                status: 'Removed',
                fullText: 'The service was not up to standard and the work was incomplete.',
                shortText: 'The service was not up to standard...',
                item: 'Lawn Maintenance',
                tags: ['Lawn care', 'Mowing', 'Fertilizing'],
                images: [],
                videos: [],
                moderationNotes: '',
                readMore: false
              }
            ],
            
            // Quote Requests data
            quoteRequests: [
              {
                id: 'QR-1',
                quoteId: 'Q-4832',
                date: '20 Oct 2025',
                business: 'Northside Plumbing',
                businessId: 'PL-3920',
                businessProfilePicture: `https://i.pravatar.cc/150?img=1`,
                title: 'Replace kitchen mixer',
                location: { suburb: 'Clayton', postcode: '3168' },
                status: 'Replied'
              },
              {
                id: 'QR-2',
                quoteId: 'Q-4821',
                date: '10 Oct 2025',
                business: 'Bolt Electrical',
                businessId: 'EL-1187',
                businessProfilePicture: `https://i.pravatar.cc/150?img=2`,
                title: 'Downlight install (6)',
                location: { suburb: 'Richmond', postcode: '3121' },
                status: 'Sent'
              },
              {
                id: 'QR-3',
                quoteId: 'Q-4798',
                date: '02 Oct 2025',
                business: 'Rapid Roofers',
                businessId: 'RO-9401',
                businessProfilePicture: `https://i.pravatar.cc/150?img=3`,
                title: 'Roof repair - small leak',
                location: { suburb: 'Hawthorn', postcode: '3122' },
                status: 'Closed – Hired'
              },
              {
                id: 'QR-4',
                quoteId: 'Q-4785',
                date: '28 Sep 2025',
                business: 'Coastal Gas',
                businessId: 'GA-2203',
                businessProfilePicture: `https://i.pravatar.cc/150?img=4`,
                title: 'Gas heater service',
                location: { suburb: 'St Kilda', postcode: '3182' },
                status: 'Closed – Not Hired'
              },
              {
                id: 'QR-5',
                quoteId: 'Q-4762',
                date: '25 Sep 2025',
                business: 'Ace Carpentry',
                businessId: 'CA-5501',
                businessProfilePicture: `https://i.pravatar.cc/150?img=5`,
                title: 'Kitchen cabinet installation',
                location: { suburb: 'Brunswick', postcode: '3056' },
                status: 'Cancelled'
              },
              {
                id: 'QR-6',
                quoteId: 'Q-4751',
                date: '18 Sep 2025',
                business: 'Modern Painters',
                businessId: 'PA-6602',
                businessProfilePicture: `https://i.pravatar.cc/150?img=6`,
                title: 'Interior painting (3 rooms)',
                location: { suburb: 'Fitzroy', postcode: '3065' },
                status: 'Expired'
              },
              {
                id: 'QR-8',
                date: '10 Sep 2025',
                business: 'Smart Locksmith',
                jobTitle: 'Door lock replacement',
                status: 'Replied'
              },
              {
                id: 'QR-9',
                date: '05 Sep 2025',
                business: 'Top Tilers',
                jobTitle: 'Bathroom tiling',
                status: 'Replied'
              },
              {
                id: 'QR-10',
                date: '01 Sep 2025',
                business: 'Clean Windows Pro',
                jobTitle: 'Window cleaning service',
                status: 'Sent'
              },
              {
                id: 'QR-11',
                date: '28 Aug 2025',
                business: 'Master Builders',
                jobTitle: 'Deck construction',
                status: 'Replied'
              },
              {
                id: 'QR-12',
                date: '22 Aug 2025',
                business: 'Elite Flooring',
                jobTitle: 'Hardwood floor installation',
                status: 'Sent'
              }
            ],
            
            // Message Threads data
            messageThreadsList: [
              {
                id: 'MSG-1',
                started: '22 Oct 2025',
                business: 'Northside Plumbing',
                businessId: 'PL-3920',
                businessProfilePicture: `https://i.pravatar.cc/150?img=1`,
                lastMessage: '23 Oct 2025',
                lastMessageText: 'Can you come Saturday?',
                status: 'Unseen'
              },
              {
                id: 'MSG-2',
                started: '09 Oct 2025',
                business: 'Bolt Electrical',
                businessId: 'EL-1187',
                businessProfilePicture: `https://i.pravatar.cc/150?img=2`,
                lastMessage: '10 Oct 2025',
                lastMessageText: 'Thanks for the quick response!',
                status: 'Seen'
              },
              {
                id: 'MSG-3',
                started: '05 Oct 2025',
                business: 'Coastal Gas',
                businessId: 'GA-2203',
                businessProfilePicture: `https://i.pravatar.cc/150?img=4`,
                lastMessage: '06 Oct 2025',
                lastMessageText: 'What time works best for you?',
                status: 'Unseen'
              },
              {
                id: 'MSG-4',
                started: '30 Sep 2025',
                business: 'Ace Carpentry',
                businessId: 'CA-5501',
                businessProfilePicture: `https://i.pravatar.cc/150?img=5`,
                lastMessage: '01 Oct 2025',
                lastMessageText: 'The quote looks good, let me check my schedule',
                status: 'Seen'
              },
              {
                id: 'MSG-5',
                started: '25 Sep 2025',
                business: 'Modern Painters',
                businessId: 'PA-6602',
                businessProfilePicture: `https://i.pravatar.cc/150?img=6`,
                lastMessage: '26 Sep 2025',
                lastMessageText: 'I have a few questions about the color options',
                status: 'Unseen'
              },
              {
                id: 'MSG-6',
                started: '20 Sep 2025',
                business: 'Green Lawn Care',
                businessId: 'LC-8801',
                businessProfilePicture: `https://i.pravatar.cc/150?img=7`,
                lastMessage: '21 Sep 2025',
                lastMessageText: 'Perfect, see you next week!',
                status: 'Seen'
              }
            ]
          };
          
          // Add additional fields for overview after arrays are defined
          const recentQuotes = customerData.recentQuotes || [];
          const recentMessages = customerData.recentMessages || [];
          const latestReviews = customerData.latestReviews || [];
          const messageThreadsList = customerData.messageThreadsList || [];
          const quoteRequests = customerData.quoteRequests || [];
          
          // Calculate direct quotes and group quotes from quoteRequests
          // For now, using default values, but can be calculated from quoteRequests if needed
          customerData.directQuotes = 54;
          customerData.groupQuotes = 12;
          
          // Get the most recent dates (arrays are already sorted by date, most recent first)
          customerData.lastQuoteActivity = recentQuotes.length > 0 ? recentQuotes[0].date : (quoteRequests.length > 0 ? quoteRequests[0].date : '14 Nov 2025');
          customerData.lastMessage = recentMessages.length > 0 ? recentMessages[0].date : (messageThreadsList.length > 0 ? messageThreadsList[0].lastMessage : '14 Nov 2025');
          customerData.lastReview = latestReviews.length > 0 ? latestReviews[0].date : (customerData.reviews && customerData.reviews.length > 0 ? customerData.reviews[0].date : '12 Nov 2025');
          
          // Calculate active conversations (messages with status 'Unseen' or 'Open')
          customerData.activeConversations = messageThreadsList.filter(msg => msg.status === 'Unseen' || msg.status === 'Open').length || 3;
          
          // Calculate unread messages (messages with status 'Unseen')
          customerData.unreadMessages = messageThreadsList.filter(msg => msg.status === 'Unseen').length || 4;
          customerData.messagesCount = messageThreadsList.length || 18;
          customerData.messageThreads = messageThreadsList.length || 18;
          if (!foundCustomer.reviewsGiven && !foundCustomer.reviewsCount) {
            customerData.reviewsGiven = Array.isArray(customerData.reviews)
              ? customerData.reviews.length
              : (customerData.reviewsPosted ?? 2);
          }
          
          customerData.moderationFlags = foundCustomer.flagCount || 0;
          customerData.fraudAlerts = 'None';
          customerData.restrictions = 'None';
          customerData.adminOwner = 'Unassigned';
          
          // Initialize arrays if they don't exist
          if (!customerData.activities) {
            customerData.activities = [
              {
                id: 'ACT-1',
                timestamp: '17 Nov 2025, 01:12 PM',
                action: 'Logged in. IP: 203.0.113.54 (Chrome – Windows)',
                details: ''
              },
              {
                id: 'ACT-2',
                timestamp: '17 Nov 2025, 12:45 PM',
                action: 'Posted review for Northside Plumbing',
                details: 'Rating: 5 stars',
                reviewId: 'RV-1',
                type: 'review'
              },
              {
                id: 'ACT-3',
                timestamp: '17 Nov 2025, 11:30 AM',
                action: 'Sent message to Bolt Electrical',
                details: ''
              },
              {
                id: 'ACT-4',
                timestamp: '17 Nov 2025, 10:15 AM',
                action: 'Requested quote from Coastal Gas',
                details: 'Gas heater service',
                quoteId: 'QR-4'
              },
              {
                id: 'ACT-5',
                timestamp: '16 Nov 2025, 03:20 PM',
                action: 'Updated profile information',
                details: ''
              },
              {
                id: 'ACT-6',
                timestamp: '16 Nov 2025, 02:10 PM',
                action: 'Posted review for Bolt Electrical',
                details: 'Rating: 4 stars',
                reviewId: 'RV-2',
                type: 'review'
              },
              {
                id: 'ACT-7',
                timestamp: '16 Nov 2025, 01:05 PM',
                action: 'Replied to message from Rapid Roofers',
                details: ''
              },
              {
                id: 'ACT-8',
                timestamp: '15 Nov 2025, 04:30 PM',
                action: 'Requested quote from Ace Carpentry',
                details: 'Kitchen cabinet installation',
                quoteId: 'QR-5'
              },
              {
                id: 'ACT-9',
                timestamp: '15 Nov 2025, 02:15 PM',
                action: 'Logged in. IP: 203.0.113.54 (Safari – iOS)',
                details: ''
              },
              {
                id: 'ACT-10',
                timestamp: '14 Nov 2025, 11:00 AM',
                action: 'Posted review for Coastal Gas',
                details: 'Rating: 5 stars',
                reviewId: 'RV-3',
                type: 'review'
              },
              {
                id: 'ACT-11',
                timestamp: '14 Nov 2025, 09:45 AM',
                action: 'Updated email address',
                details: ''
              },
              {
                id: 'ACT-12',
                timestamp: '13 Nov 2025, 05:20 PM',
                action: 'Requested quote from Modern Painters',
                details: 'Interior painting (3 rooms)',
                quoteId: 'QR-6'
              },
              {
                id: 'ACT-13',
                timestamp: '13 Nov 2025, 03:10 PM',
                action: 'Sent message to Northside Plumbing',
                details: ''
              },
              {
                id: 'ACT-14',
                timestamp: '12 Nov 2025, 02:30 PM',
                action: 'Phone number verified successfully',
                details: ''
              },
              {
                id: 'ACT-15',
                timestamp: '12 Nov 2025, 01:15 PM',
                action: 'Logged in. IP: 203.0.113.54 (Chrome – Windows)',
                details: ''
              },
              {
                id: 'ACT-16',
                timestamp: '11 Nov 2025, 04:00 PM',
                action: 'Updated profile picture',
                details: ''
              },
              {
                id: 'ACT-17',
                timestamp: '11 Nov 2025, 11:30 AM',
                action: 'Replied to message from Bolt Electrical',
                details: ''
              },
              {
                id: 'ACT-18',
                timestamp: '10 Nov 2025, 03:45 PM',
                action: 'Account created',
                details: 'Member since 10 Nov 2025'
              }
            ];
          }
          if (!customerData.auditLogs) {
            // Pick one of several scenarios by customer so each record shows different logs (4 rows each)
            const idStr = String(customerId || customerData.customerId || customerData.id || '1');
            const scenarioIndex = idStr.split('').reduce((a, c) => a + c.charCodeAt(0), 0) % 3;
            const scenarios = [
              [
                {
                  id: 'LOG-1',
                  timestamp: '5 Aug 2026, 9:10 am',
                  performedBy: 'Customer — Olivia Carter',
                  userType: 'Customer',
                  action: 'Login Successful',
                  section: 'Security',
                  summary: 'Customer signed in from a trusted device',
                  relatedRecord: '—',
                },
                {
                  id: 'LOG-2',
                  timestamp: '4 Aug 2026, 10:15 am',
                  performedBy: 'System',
                  userType: 'System',
                  action: 'Flag Handling Updated',
                  section: 'Flags',
                  summary: 'Moderation case MOD-000701 marked Active after decision work began',
                  relatedRecord: 'MOD-000701',
                },
                {
                  id: 'LOG-3',
                  timestamp: '28 Jul 2026, 2:40 pm',
                  performedBy: 'Admin — Mark Johnson',
                  userType: 'Admin Manager',
                  action: 'Send Password Reset',
                  section: 'Security',
                  summary: 'Password-reset link requested for the registered Customer email',
                  relatedRecord: '—',
                },
                {
                  id: 'LOG-4',
                  timestamp: '12 Jan 2026, 10:11 am',
                  performedBy: 'Customer — Olivia Carter',
                  userType: 'Customer',
                  action: 'Account Created',
                  section: 'Onboarding',
                  summary: 'Customer account created successfully during signup',
                  relatedRecord: '—',
                },
              ],
              [
                {
                  id: 'LOG-1',
                  timestamp: '5 Aug 2026, 7:40 am',
                  performedBy: 'System',
                  userType: 'System',
                  action: 'Verification Status Unchanged',
                  section: 'Verification',
                  summary: 'Verification remained Pending; account stayed Active within the verification window',
                  relatedRecord: '—',
                },
                {
                  id: 'LOG-2',
                  timestamp: '2 Aug 2026, 11:00 am',
                  performedBy: 'Admin — Alex Roberts',
                  userType: 'Admin Manager',
                  action: 'Force Logout',
                  section: 'Security',
                  summary: 'All active sessions ended for this Customer; Account Status unchanged',
                  relatedRecord: '—',
                },
                {
                  id: 'LOG-3',
                  timestamp: '1 Aug 2026, 9:00 am',
                  performedBy: 'Customer — Liam Foster',
                  userType: 'Customer',
                  action: 'Account Created',
                  section: 'Onboarding',
                  summary: 'Customer account created successfully during signup',
                  relatedRecord: '—',
                },
                {
                  id: 'LOG-4',
                  timestamp: '1 Aug 2026, 9:05 am',
                  performedBy: 'System',
                  userType: 'System',
                  action: 'Verification Status Set',
                  section: 'Verification',
                  summary: 'Verification Status set to Pending on account creation',
                  relatedRecord: '—',
                },
              ],
              [
                {
                  id: 'LOG-1',
                  timestamp: '20 Jul 2026, 11:30 am',
                  performedBy: 'System',
                  userType: 'System',
                  action: 'Account Status Changed',
                  section: 'Account',
                  summary: 'Account Status changed to Restricted following a decided Moderation case',
                  relatedRecord: 'MOD-000688',
                },
                {
                  id: 'LOG-2',
                  timestamp: '18 Jul 2026, 3:12 pm',
                  performedBy: 'Admin — Mark Johnson',
                  userType: 'Admin Manager',
                  action: 'Send Password Reset',
                  section: 'Security',
                  summary: 'Password-reset link requested for the registered Customer email',
                  relatedRecord: '—',
                },
                {
                  id: 'LOG-3',
                  timestamp: '15 Jul 2026, 8:25 am',
                  performedBy: 'System',
                  userType: 'System',
                  action: 'Flag Handling Updated',
                  section: 'Flags',
                  summary: 'Flag FLG-61004 linked to Moderation case MOD-000688',
                  relatedRecord: 'FLG-61004',
                },
                {
                  id: 'LOG-4',
                  timestamp: '18 Mar 2026, 10:00 am',
                  performedBy: 'Customer — Noah Williams',
                  userType: 'Customer',
                  action: 'Account Created',
                  section: 'Onboarding',
                  summary: 'Customer account created successfully during signup',
                  relatedRecord: '—',
                },
              ],
            ];
            customerData.auditLogs = scenarios[scenarioIndex];
          }
          const accountRef =
            customerData.customerId || customerData.id || String(customerId || '');
          customerData.auditLogs = refreshCustomerAuditLogs(
            accountRef,
            customerData.auditLogs || []
          );
          if (!customerData.notes) {
            customerData.notes = [
              {
                id: 'NOTE-1',
                createdAt: '2025-11-17T09:15:00',
                adminName: 'Support — Sarah Lee',
                team: 'Support',
                noteContent: 'Customer reported issue with messaging feature. Followed up and resolved. Customer was satisfied with the response time.',
                category: 'Support',
                author: 'Sarah Lee'
              },
              {
                id: 'NOTE-2',
                createdAt: '2025-11-15T14:30:00',
                adminName: 'Compliance — Mark Johnson',
                team: 'Compliance',
                noteContent: 'Account flagged for review due to multiple failed login attempts. Security hold applied. Customer contacted and verified identity.',
                category: 'Fraud',
                author: 'Mark Johnson'
              },
              {
                id: 'NOTE-3',
                createdAt: '2025-11-10T11:20:00',
                adminName: 'Moderator — Alex Roberts',
                team: 'Moderator',
                noteContent: 'Customer requested refund for cancelled service. Processed refund and updated billing records. Customer confirmed receipt.',
                category: 'Billing',
                author: 'Alex Roberts'
              }
            ];
          }
          if (!Array.isArray(customerData.flags) || customerData.flags.length === 0) {
            customerData.flags = CUSTOMER_ACCOUNT_FLAG_SAMPLES;
            customerData.openFlagsCount = CUSTOMER_ACCOUNT_FLAG_SAMPLES.filter((f) => {
              const s = String(f.status || '').toLowerCase().trim();
              return (
                s === 'new' ||
                s === 'active' ||
                s === 'open' ||
                s === 'under review' ||
                s === 'escalated'
              );
            }).length;
            customerData.flagCount = customerData.openFlagsCount;
          }
          if (!customerData.restrictionHistory) {
            customerData.restrictionHistory = [
              {
                date: '12 Dec 2025',
                action: 'Messaging restricted',
                admin: 'J.Smith'
              },
              {
                date: '18 Dec 2025',
                action: 'Messaging restriction lifted',
                admin: 'J.Smith'
              }
            ];
          }
          
          setCustomer(customerData);
          setLoading(false);
          return;
        }
        
        // If no customer data passed, show error (for direct URL access without navigation)
        setCustomer(null);
        setLoading(false);
        return;
      } catch (error) {
        console.error('Error fetching customer detail:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCustomerDetail();
  }, [customerId, location.state]);

  // Open tab from navigation state (e.g. from moderation queue → user detail flags tab)
  useEffect(() => {
    const tabFromState = location.state?.activeTab;
    if (tabFromState && ['overview', 'flags', 'security'].includes(tabFromState)) {
      setActiveTab(tabFromState);
    }
  }, [location.state?.activeTab]);

  // Handle hash navigation to activate tabs
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash) {
        const hashToTabMap = {
          flags: 'flags',
          security: 'security',
          overview: 'overview',
          'account-status': 'overview',
        };

        const tabId = hashToTabMap[hash];
        const validTabIds = ['overview', 'flags', 'security'];
        if (tabId && validTabIds.includes(tabId)) {
          setActiveTab(tabId);
        }
      }
    };

    // Check hash on mount and when location changes
    handleHashChange();

    // Listen for hash changes (browser back/forward)
    window.addEventListener('hashchange', handleHashChange);
    
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [location.hash]);

  // Tags Display Component - dynamically shows tags that fit
  const TagsDisplay = ({ tags }) => {
    const containerRef = useRef(null);
    const [visibleCount, setVisibleCount] = useState(tags.length);
    const [remainingCount, setRemainingCount] = useState(0);

    useEffect(() => {
      const calculateVisibleTags = () => {
        if (!containerRef.current || tags.length === 0) return;

        const container = containerRef.current;
        const containerWidth = container.offsetWidth;
        const gap = 8; // gap-2 = 8px
        const plusTagWidth = 50; // Approximate width for "+X" tag
        
        let totalWidth = 0;
        let count = 0;

        // Create a temporary element to measure tag widths
        const tempDiv = document.createElement('div');
        tempDiv.style.position = 'absolute';
        tempDiv.style.visibility = 'hidden';
        tempDiv.style.whiteSpace = 'nowrap';
        tempDiv.className = 'border border-solid border-mediumgray rounded-full px-3 py-1 text-xs';
        document.body.appendChild(tempDiv);

        for (let i = 0; i < tags.length; i++) {
          tempDiv.textContent = tags[i];
          const tagWidth = tempDiv.offsetWidth;
          
          // Check if this tag + gap + plus tag (if needed) fits
          const neededWidth = totalWidth + tagWidth + (i > 0 ? gap : 0);
          const wouldNeedPlusTag = i < tags.length - 1;
          const totalNeeded = neededWidth + (wouldNeedPlusTag ? gap + plusTagWidth : 0);

          if (totalNeeded <= containerWidth) {
            totalWidth = neededWidth;
            count++;
          } else {
            break;
          }
        }

        document.body.removeChild(tempDiv);
        setVisibleCount(count);
        setRemainingCount(tags.length - count);
      };

      calculateVisibleTags();
      window.addEventListener('resize', calculateVisibleTags);
      return () => window.removeEventListener('resize', calculateVisibleTags);
    }, [tags]);

    return (
      <div ref={containerRef} className="flex items-center gap-2 overflow-hidden">
        {tags.slice(0, visibleCount).map((tag, tagIndex) => (
          <span 
            key={tagIndex} 
            className="border border-solid border-mediumgray rounded-full px-3 py-1 cursor-pointer transition-all duration-200 hover:bg-offblack hover:text-white hover:border-offblack text-xs text-offblack flex-shrink-0"
          >
            {tag}
          </span>
        ))}
        {remainingCount > 0 && (
          <span className="border border-solid border-mediumgray rounded-full px-3 py-1 text-xs text-offblack flex-shrink-0">
            +{remainingCount}
          </span>
        )}
      </div>
    );
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Format datetime helper
  const formatDateTime = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return (
      date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) +
      ', ' +
      date.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true })
    );
  };

  // Format last login for Security tab: "28 Jan 2026 · 09:12"
  const formatLastLogin = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    const datePart = date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
    const timePart = date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false });
    return `${datePart} · ${timePart}`;
  };

  // Format mobile number
  const formatMobile = (mobile) => {
    if (!mobile) return '—';
    // Format as +61 4XX XXX XXX
    const cleaned = mobile.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `+61 ${cleaned.substring(1, 4)} ${cleaned.substring(4, 7)} ${cleaned.substring(7)}`;
    }
    return mobile;
  };

  // Format flags display
  const formatFlags = (customer) => {
    if (customer.underReview) {
      return 'Under Review';
    }
    if (customer.flagCount && customer.flagCount > 0) {
      return `Flagged (${customer.flagCount})`;
    }
    return 'None';
  };

  // Generate flags array from customer flag properties
  const getCustomerFlags = (customer) => {
    const flags = [];
    
    // If customer has flags based on flagCount
    if (customer.flagCount && customer.flagCount > 0) {
      for (let i = 0; i < customer.flagCount; i++) {
        const linkedCaseId = customer.flagLinkedCaseId || null;
        const linkedCaseType = customer.flagLinkedCaseType || null;
        // Use linkedCaseId as flagId if available, otherwise generate based on category
        let flagId = linkedCaseId;
        if (!flagId) {
          const category = customer.flagCategory || 'Moderation';
          if (category === 'Fraud') flagId = `F-${String(customer.customerId || customer.id).padStart(5, '0')}${i + 1}`;
          else if (category === 'Security') flagId = `SEC-${String(customer.customerId || customer.id).padStart(4, '0')}${i + 1}`;
          else flagId = `MC-${String(customer.customerId || customer.id).padStart(4, '0')}${i + 1}`;
        }
        
        flags.push({
          id: `flag-${customer.customerId || customer.id}-${i + 1}`,
          flagId: flagId,
          source: customer.flagSource || 'System',
          category: customer.flagCategory || 'Moderation',
          reason: customer.flagReason || 'Policy violation',
          severity: customer.flagSeverity || 'Medium',
          status: customer.underReview ? 'Under Review' : 'Open',
          linkedCaseId: linkedCaseId,
          linkedCaseType: linkedCaseType,
          createdAt: customer.flagCreatedAt || customer.createdAt || new Date().toISOString(),
          updatedAt: customer.flagUpdatedAt || customer.updatedAt || new Date().toISOString()
        });
      }
    }
    
    // If customer is under review, add a review flag
    if (customer.underReview && !flags.find(f => f.status === 'Under Review')) {
      const reviewCaseId = customer.reviewCaseId || `MC-${String(customer.customerId || customer.id).padStart(4, '0')}-REV`;
      flags.push({
        id: `flag-${customer.customerId || customer.id}-review`,
        flagId: reviewCaseId,
        source: 'Admin',
        category: 'Moderation',
        reason: 'Account under review',
        severity: 'High',
        status: 'Under Review',
        linkedCaseId: reviewCaseId,
        linkedCaseType: 'Moderation',
        createdAt: customer.reviewStartedAt || customer.createdAt || new Date().toISOString(),
        updatedAt: customer.updatedAt || new Date().toISOString()
      });
    }
    
    // If customer is flagged but no flagCount, add a generic flag
    if (customer.flagged && flags.length === 0) {
      const securityCaseId = `SEC-${String(customer.customerId || customer.id).padStart(4, '0')}`;
      flags.push({
        id: `flag-${customer.customerId || customer.id}-flagged`,
        flagId: securityCaseId,
        source: 'System',
        category: 'Security',
        reason: 'Account flagged',
        severity: 'Medium',
        status: 'Open',
        linkedCaseId: securityCaseId,
        linkedCaseType: 'Security',
        createdAt: customer.flaggedAt || customer.createdAt || new Date().toISOString(),
        updatedAt: customer.updatedAt || new Date().toISOString()
      });
    }
    
    return flags;
  };

  const OPEN_FLAG_OVERVIEW = new Set(['new', 'active', 'open', 'under review', 'escalated']);

  const getFlagSourcesOverview = (c) => {
    if (c.moderationAndFlags?.length) return c.moderationAndFlags;
    if (c.flags?.length) return c.flags;
    return getCustomerFlags(c);
  };

  const getOpenFlagsOverviewCount = (c) => {
    if (typeof c.openFlagsCount === 'number') return c.openFlagsCount;
    const arr = getFlagSourcesOverview(c);
    return arr.filter((f) =>
      OPEN_FLAG_OVERVIEW.has(String(f.status || '').toLowerCase().trim())
    ).length;
  };

  const getOverviewRestrictionStatus = (c) => {
    if (
      c.restrictionStatus &&
      ['None', 'Messaging Restricted', 'Login Restricted'].includes(c.restrictionStatus)
    ) {
      return c.restrictionStatus;
    }
    const list = c.accountRestrictions || [];
    let hasLogin = false;
    let hasMessaging = false;
    list.forEach((r) => {
      const t = (r.type || '').toLowerCase();
      if (t.includes('login') || t === 'login blocked' || (t.includes('block') && t.includes('login'))) {
        hasLogin = true;
      }
      if (t.includes('messaging') || t.includes('message') || t.includes('messaging blocked')) {
        hasMessaging = true;
      }
    });
    if (hasLogin) return 'Login Restricted';
    if (hasMessaging) return 'Messaging Restricted';
    return 'None';
  };

  const normalizeFlagIdOverview = (id, index = 0) => {
    if (!id) return `FLG-${String(10000 + index).padStart(5, '0')}`;
    if (typeof id === 'string' && id.startsWith('FLG-')) return id;
    const match = id.toString().match(/(\d+)/);
    if (match) return `FLG-${String(match[1]).padStart(5, '0')}`;
    const hash = id.toString().split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return `FLG-${String((hash % 99999) + 10000).padStart(5, '0')}`;
  };

  const getLatestFlagOverview = (c) => {
    const sources = getFlagSourcesOverview(c);
    if (!sources.length) return null;
    const open = sources.filter((f) =>
      OPEN_FLAG_OVERVIEW.has(String(f.status || '').toLowerCase().trim())
    );
    const pool = open.length ? open : sources;
    return [...pool].sort((a, b) => {
      const da = new Date(a.updatedAt || a.lastUpdated || a.createdAt || 0).getTime();
      const db = new Date(b.updatedAt || b.lastUpdated || b.createdAt || 0).getTime();
      return db - da;
    })[0];
  };

  const formatRestrictionAppliedOverview = (c) => {
    if (getOverviewRestrictionStatus(c) === 'None') return '—';
    if (c.restrictionAppliedOn) return formatDate(c.restrictionAppliedOn);
    const list = c.accountRestrictions || [];
    const withApplied = list.find((r) => r.applied);
    if (withApplied?.applied) {
      if (typeof withApplied.applied === 'string' && withApplied.applied.match(/^\d/)) {
        return withApplied.applied.replace(/\s+\d{1,2}:\d{2}\s*(am|pm)$/i, '').trim();
      }
      return formatDate(withApplied.applied);
    }
    return '—';
  };

  const formatRestrictionExpiresOverview = (c) => {
    if (getOverviewRestrictionStatus(c) === 'None') return '—';
    if (c.restrictionExpiresOn) return formatDate(c.restrictionExpiresOn);
    const list = c.accountRestrictions || [];
    const withExp = list.find((r) => r.expires);
    if (withExp?.expires) return formatDate(withExp.expires);
    return '—';
  };

  const getAccountStatusOverview = (c) => {
    const raw = String(c?.status || c?.accountStatus || '').trim();
    if (!raw) return 'Active';
    const lower = raw.toLowerCase();
    if (lower === 'active') return 'Active';
    if (lower === 'inactive' || lower === 'deleted') return 'Inactive';
    if (lower === 'restricted' || lower === 'under review' || lower === 'suspended') return 'Restricted';
    if (lower === 'banned' || lower === 'blocked') return 'Banned';
    if (lower === 'closed') return 'Closed';
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  };

  const getVerificationStatusOverview = (c) => {
    const raw = String(c?.verificationStatus || '').trim();
    if (raw) {
      const lower = raw.toLowerCase();
      if (lower === 'verified') return 'Verified';
      if (lower === 'pending') return 'Pending';
      return raw.charAt(0).toUpperCase() + raw.slice(1);
    }
    if (c?.emailVerified) return 'Verified';
    return 'Pending';
  };

  const formatCustomerIdDisplay = (c) => {
    const id = c?.customerId || c?.id;
    if (!id) return '';
    if (typeof id === 'string' && id.startsWith('CUS-')) return id;
    const numericPart = id.toString().replace(/\D/g, '');
    return numericPart ? `CUS-${numericPart.padStart(5, '0')}` : String(id);
  };

  const handleCopyCustomerId = async () => {
    const id = formatCustomerIdDisplay(customer);
    if (!id) return;
    try {
      await navigator.clipboard.writeText(id);
      setCustomerIdCopied(true);
      window.setTimeout(() => setCustomerIdCopied(false), 1500);
    } catch {
      // Clipboard unavailable — leave UI unchanged
    }
  };

  const handleCopyContactField = async (field, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedContactField(field);
      window.setTimeout(() => setCopiedContactField(null), 1500);
    } catch {
      // Clipboard unavailable — leave UI unchanged
    }
  };

  const getOverviewLocation = (c) => {
    const suburb = c?.location?.suburb || c?.suburb || '';
    const state = c?.region || c?.location?.state || c?.state || '';
    const postcode = c?.location?.postcode || c?.postcode || '';
    if (suburb && state && postcode) return `${suburb}, ${state} ${postcode}`;
    if (suburb && postcode) return `${suburb} ${postcode}`;
    if (suburb && state) return `${suburb}, ${state}`;
    return suburb || state || postcode || '—';
  };

  const getOverviewReviewsCount = (c) =>
    Number(c?.reviewsGiven ?? c?.reviewsCount ?? c?.reviewsPosted ?? 0) || 0;

  const getOverviewMessagesCount = (c) => {
    if (Array.isArray(c?.messageThreadsList) && c.messageThreadsList.length > 0) {
      return c.messageThreadsList.length;
    }
    if (typeof c?.messagesCount === 'number') return c.messagesCount;
    if (typeof c?.messageThreads === 'number') return c.messageThreads;
    return Number(c?.messagesStarted ?? 0) || 0;
  };

  const getOverviewQuotesCount = (c) =>
    Number(c?.directQuotes ?? c?.quotesCount ?? c?.quoteRequestsCount ?? 0) || 0;

  const getOverviewSupportTickets = (c) =>
    Number(c?.openSupportTickets ?? c?.supportTickets ?? 0) || 0;

  const getOverviewActiveAppeals = (c) => Number(c?.activeAppeals ?? 0) || 0;

  const renderOverviewLinkedField = ({
    label,
    valueText,
    enabled,
    onNavigate,
  }) => (
    <div className="space-y-1">
      <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
      {enabled ? (
        <AdminDetailFieldValue>
          <AdminLinkAction
            type="button"
            onClick={onNavigate}
            className="tabular-nums text-left"
          >
            {valueText}
          </AdminLinkAction>
        </AdminDetailFieldValue>
      ) : (
        <AdminDetailFieldValue className="tabular-nums text-offblack">
          {valueText}
        </AdminDetailFieldValue>
      )}
    </div>
  );

  const getLinkedCaseOverview = (c) => {
    const latest = getLatestFlagOverview(c);
    return latest?.linkedCaseId || latest?.caseId || c.linkedCaseId || '—';
  };

  const getCurrentReviewStatusOverview = (c) => {
    if (c.currentReviewStatus && String(c.currentReviewStatus).trim() !== '') {
      return c.currentReviewStatus;
    }
    if (c.status === 'Under Review' || c.underReview) return 'Under Review';
    const latest = getLatestFlagOverview(c);
    if (latest) {
      const fs = String(latest.status || '').toLowerCase();
      if (fs.includes('under review')) return 'Under Review';
      if (fs === 'open' || fs === 'new') return 'Open';
    }
    return 'None';
  };

  /** Flags tab sources: User Flag or System Flag (Team Flags are not supported). */
  const mapFlagSourceToSpec = (raw, ctx = {}) => {
    const from = String(raw || ctx.source || '').trim();
    const type = String(ctx.type || ctx.category || ctx.contentType || '').toLowerCase();
    const lowered = from.toLowerCase();

    if (
      lowered === 'user flag' ||
      lowered === 'customer' ||
      lowered === 'business' ||
      lowered.includes('user report') ||
      lowered.includes('user flag')
    ) {
      return 'User Flag';
    }

    if (
      lowered === 'system flag' ||
      lowered === 'system' ||
      lowered === 'auto' ||
      type.includes('auto-flag') ||
      type.includes('system flag')
    ) {
      return 'System Flag';
    }

    // Message / Quote threads are recipient User Flags only (not System-scanned)
    if (
      type.includes('message') ||
      type.includes('quote') ||
      ctx.messageId ||
      ctx.quoteId
    ) {
      return 'User Flag';
    }

    return 'System Flag';
  };

  const normalizeFlagReasonText = (flag) => {
    const t = (flag.reason || flag.trigger || flag.object || '').toString().trim();
    return t || '—';
  };

  const normalizeFlagSeveritySpec = (s) => {
    let v = String(s || 'Medium').trim();
    if (v === 'Critical') v = 'High';
    if (!['Low', 'Medium', 'High'].includes(v)) v = 'Medium';
    return v;
  };

  const getFlagSeverityTextClass = (severity) => {
    const s = String(severity || '').trim();
    if (s === 'High' || s === 'Critical' || s === 'Severe') return 'text-alertred';
    if (s === 'Medium') return 'text-warmyellow';
    if (s === 'Low') return 'text-freshgreen';
    return '';
  };

  const normalizeFlagStatusSpec = (status) => {
    const v = String(status || 'New').trim();
    const lower = v.toLowerCase();
    if (['decided', 'resolved', 'approved', 'closed'].includes(lower)) return 'Decided';
    if (['active', 'under review', 'escalated', 'attached'].includes(lower)) return 'Active';
    if (['new', 'open'].includes(lower)) return 'New';
    if (v === 'New' || v === 'Active' || v === 'Decided') return v;
    return 'New';
  };

  const normalizeCustomerFlagId = (id, index = 0) => {
    if (!id) return `FLG-${String(10000 + index).padStart(5, '0')}`;
    if (typeof id === 'string' && id.startsWith('FLG-')) return id;
    const match = id.toString().match(/(\d+)/);
    if (match) return `FLG-${String(match[1]).padStart(5, '0')}`;
    const hash = id.toString().split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return `FLG-${String((hash % 99999) + 10000).padStart(5, '0')}`;
  };

  const normalizeModerationCaseId = (id) => {
    if (!id) return '';
    const raw = String(id).trim();
    if (/^MOA-/i.test(raw)) return raw.toUpperCase();
    if (/^(?:MODS|MOD)-/i.test(raw)) {
      const digits = raw.replace(/^(?:MODS|MOD)-?/i, '').replace(/\D/g, '');
      if (digits) return `MOD-${digits.padStart(6, '0')}`;
      return raw.toUpperCase();
    }
    const digits = raw.replace(/\D/g, '');
    if (digits) return `MOD-${digits.padStart(6, '0')}`;
    return raw;
  };

  /** Resolve case ID + Open Case action for a flag row. */
  const resolveFlagHandling = (flag) => {
    const linkedCaseId = flag.linkedCaseId || flag.caseId || null;
    const handlingStatus = normalizeFlagStatusSpec(flag.handlingStatus || flag.status);
    const caseId = normalizeModerationCaseId(linkedCaseId) || '—';
    const isDecided = handlingStatus === 'Decided';

    return {
      handlingRecord: caseId,
      handlingRecordId: caseId !== '—' ? caseId : null,
      handlingStatus,
      actionLabel: caseId !== '—' ? 'Open Case' : null,
      actionPath:
        caseId !== '—'
          ? `/admin/moderation/queue/${encodeURIComponent(caseId)}`
          : null,
      openFromArchive: isDecided,
    };
  };

  const buildCustomerDetailFlagRows = (c) => {
    const mapRow = (item, index) => {
      const handling = resolveFlagHandling(item);
      return {
        key: normalizeCustomerFlagId(item.flagId || item.id, index),
        flagId: normalizeCustomerFlagId(item.flagId || item.id, index),
        source: mapFlagSourceToSpec(item.source || item.type, item),
        reason: normalizeFlagReasonText({
          reason: item.reason,
          trigger: item.trigger,
          object: item.object,
        }),
        severity: normalizeFlagSeveritySpec(item.severity),
        handlingRecord: handling.handlingRecord,
        handlingRecordId: handling.handlingRecordId,
        handlingStatus: handling.handlingStatus,
        actionLabel: handling.actionLabel,
        actionPath: handling.actionPath,
        openFromArchive: handling.openFromArchive,
        createdAt: item.openedOn || item.created || item.createdAt,
        updatedAt: item.lastUpdated || item.lastUpdatedAt || item.updatedAt || item.openedOn || item.created,
      };
    };

    if (c.moderationAndFlags?.length) {
      return c.moderationAndFlags.map(mapRow);
    }
    const fallbackFlags = c.flags || getCustomerFlags(c);
    return fallbackFlags.map(mapRow);
  };

  const handleFlagRowAction = (flag) => {
    if (!flag?.actionPath) return;
    navigate(flag.actionPath, {
      state: {
        customerId: formatCustomerIdDisplay(customer),
        flagId: flag.flagId,
        handlingRecordId: flag.handlingRecordId,
        fromArchive: !!flag.openFromArchive,
      },
    });
  };

  const getSecurityActiveSessions = (c) =>
    Number(c?.currentActiveSessions ?? c?.activeSessions ?? 0) || 0;

  const getSecurityCustomerDisplayName = (c) =>
    `${c?.firstName || ''} ${c?.lastName || ''}`.trim() || 'this customer';

  const appendSecurityAuditLog = (prev, action, summary) => {
    const accountRef = prev.customerId || prev.id;
    recordAccountActivity({
      accountRef,
      accountType: 'Customer',
      changeType: action,
      activityDetails: summary,
      changedBy: 'Admin — Sarah Lee',
      itemId: accountRef,
    });
    return mapCentralAuditToLocal(getAccountAuditLogRows(accountRef));
  };

  const openForceLogoutConfirm = () => {
    const sessions = getSecurityActiveSessions(customer);
    if (sessions <= 0) return;
    const name = getSecurityCustomerDisplayName(customer);
    setSecurityConfirm({
      type: 'forceLogout',
      title: 'Force Logout',
      message: `Force logout ${name} from all active sessions?`,
      confirmLabel: 'Confirm Force Logout',
    });
  };

  const openPasswordResetConfirm = () => {
    const name = getSecurityCustomerDisplayName(customer);
    setSecurityConfirm({
      type: 'passwordReset',
      title: 'Send Password Reset',
      message: `Send a password-reset link to the registered email for ${name}?`,
      confirmLabel: 'Send Reset Link',
    });
  };

  const handleSecurityConfirm = () => {
    if (!securityConfirm) return;
    if (securityConfirm.type === 'forceLogout') {
      setCustomer((prev) => ({
        ...prev,
        currentActiveSessions: 0,
        activeSessions: 0,
        auditLogs: appendSecurityAuditLog(
          prev,
          'Force Logout',
          'All active sessions terminated for this customer account'
        ),
      }));
    } else if (securityConfirm.type === 'passwordReset') {
      setCustomer((prev) => ({
        ...prev,
        auditLogs: appendSecurityAuditLog(
          prev,
          'Send Password Reset',
          'Password-reset link sent to the registered customer email'
        ),
      }));
    }
    setSecurityConfirm(null);
  };

  const getDetailTablePagination = (totalCount, currentPage) => {
    const totalPages = Math.max(1, Math.ceil(totalCount / CONFIG_LIST_PAGE_SIZE));
    const effectivePage = Math.min(currentPage, totalPages);
    const startIndex = (effectivePage - 1) * CONFIG_LIST_PAGE_SIZE;
    return {
      totalPages,
      effectivePage,
      rangeStart: totalCount === 0 ? 0 : startIndex + 1,
      rangeEnd: Math.min(effectivePage * CONFIG_LIST_PAGE_SIZE, totalCount),
      showPagination: totalCount > CONFIG_LIST_PAGE_SIZE,
      paginate: (items) => items.slice(startIndex, startIndex + CONFIG_LIST_PAGE_SIZE),
    };
  };

  useEffect(() => {
    if (!customer) return;
    const flagsCount = buildCustomerDetailFlagRows(customer).length;
    setFlagsCurrentPage((p) =>
      Math.min(p, Math.max(1, Math.ceil(flagsCount / CONFIG_LIST_PAGE_SIZE)))
    );
  }, [customer]);

  // Format activity display
  const formatActivity = (customer) => {
    const reviews = customer.reviewsCount || 0;
    const quotes = customer.quotesCount || 0;
    const messages = customer.messagesCount || 0;
    return `Reviews ${reviews}\nQuotes ${quotes}\nMessages ${messages}`;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <AdminSupportingText>Loading customer details…</AdminSupportingText>
        </div>
      </AdminLayout>
    );
  }

  if (!customer) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <AdminAlertText className="text-alertred mb-4 block">Customer not found</AdminAlertText>
            <AdminRowAction onClick={() => navigate('/admin/users/customers')}>
              Back to Customers
            </AdminRowAction>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const customerFlagRows = buildCustomerDetailFlagRows(customer);
  const flagsTablePagination = getDetailTablePagination(customerFlagRows.length, flagsCurrentPage);
  const paginatedFlagRows = flagsTablePagination.paginate(customerFlagRows);

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'flags', label: 'Flags' },
    { id: 'security', label: 'Security' },
    { id: 'audit-log', label: 'Audit Log' },
  ];

  const overviewCustomerId = formatCustomerIdDisplay(customer);
  const overviewAccountStatus = getAccountStatusOverview(customer);
  const overviewVerificationStatus = getVerificationStatusOverview(customer);
  const overviewMemberSince = customer.joined
    ? formatDate(customer.joined)
    : customer.signupMonth || '—';
  const overviewLastActive = formatDateTime(
    customer.lastActive || customer.lastLogin || customer.devices?.[0]?.date
  );
  const headerDisplayName = `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || '—';

  return (
    <AdminLayout>
      <div className="tp-container-main">
        <div>

        {/* Persistent Customer Header — title only */}
        <DashboardMainContainer className={ADMIN_DETAIL_MAIN_HEADER_CLASS}>
          <div className="flex items-center gap-4">
            <AdminProfileAvatar
              src={customer.profilePicture || Profile}
              alt={headerDisplayName}
              onError={(e) => {
                e.target.src = Profile;
              }}
            />
            <div className="flex-1 min-w-0">
              <AdminPanelTitle>{headerDisplayName}</AdminPanelTitle>
            </div>
          </div>
        </DashboardMainContainer>

        {/* Tabs */}
        <DashboardMainContainer className="!bg-white">
          <div className="border-b border-lightgray">
            <div className="flex space-x-8 overflow-x-auto">
              {tabs.map((tab) => (
                <AdminTabButton
                  key={tab.id}
                  active={activeTab === tab.id}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </AdminTabButton>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div
            className={
              activeTab === 'flags' || activeTab === 'audit-log'
                ? `${ADMIN_TAB_CONTENT_OFFSET_CLASS} w-full min-w-0`
                : ADMIN_TAB_CONTENT_CONTAINER_CLASS
            }
          >
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {/* Column 1 — Customer Details */}
                    <div className="space-y-4">
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Customer ID</AdminDetailFieldLabel>
                        <div className="flex items-center gap-2 min-w-0">
                          <AdminDetailFieldValue as="div" className="truncate min-w-0">
                            {overviewCustomerId || '—'}
                          </AdminDetailFieldValue>
                          {overviewCustomerId ? (
                            <button
                              type="button"
                              onClick={handleCopyCustomerId}
                              className="inline-flex items-center justify-center shrink-0 text-mediumgray hover:text-primaryblue"
                              title={customerIdCopied ? 'Copied' : 'Copy Customer ID'}
                              aria-label={customerIdCopied ? 'Copied' : 'Copy Customer ID'}
                            >
                              <i
                                className={`fi ${
                                  customerIdCopied ? 'fi-rr-check text-freshgreen' : 'fi-rr-copy'
                                } text-xs leading-none`}
                                aria-hidden
                              />
                            </button>
                          ) : null}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Account Status</AdminDetailFieldLabel>
                        <AdminStatusBadge
                          status={overviewAccountStatus}
                          label={overviewAccountStatus}
                        />
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Verification Status</AdminDetailFieldLabel>
                        <AdminDetailFieldValue
                          className={
                            overviewVerificationStatus === 'Verified'
                              ? 'text-freshgreen'
                              : 'text-warmyellow'
                          }
                        >
                          {overviewVerificationStatus}
                        </AdminDetailFieldValue>
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Member Since</AdminDetailFieldLabel>
                        <AdminDetailFieldValue>{overviewMemberSince}</AdminDetailFieldValue>
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Last Active</AdminDetailFieldLabel>
                        <AdminDetailFieldValue>{overviewLastActive}</AdminDetailFieldValue>
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Customer Email</AdminDetailFieldLabel>
                        <div className="flex items-center gap-2 min-w-0">
                          <AdminDetailFieldValue as="div" className="truncate min-w-0" title={customer.email || '—'}>
                            {customer.email || '—'}
                          </AdminDetailFieldValue>
                          {customer.email && customer.emailVerified ? (
                            <i
                              className="fi fi-sr-check-circle text-freshgreen text-xs leading-none shrink-0"
                              title="Verified"
                              aria-label="Verified"
                            />
                          ) : null}
                          {customer.email ? (
                            <button
                              type="button"
                              onClick={() => handleCopyContactField('email', customer.email)}
                              className="inline-flex items-center justify-center shrink-0 text-mediumgray hover:text-primaryblue"
                              title={copiedContactField === 'email' ? 'Copied' : 'Copy email'}
                              aria-label={copiedContactField === 'email' ? 'Copied' : 'Copy email'}
                            >
                              <i
                                className={`fi ${
                                  copiedContactField === 'email'
                                    ? 'fi-rr-check text-freshgreen'
                                    : 'fi-rr-copy'
                                } text-xs leading-none`}
                                aria-hidden
                              />
                            </button>
                          ) : null}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Mobile</AdminDetailFieldLabel>
                        <div className="flex items-center gap-2 min-w-0">
                          {customer.mobile ? (
                            <AdminDetailFieldValue
                              as="span"
                              className="truncate min-w-0"
                              title={formatMobile(customer.mobile)}
                            >
                              {formatMobile(customer.mobile)}
                            </AdminDetailFieldValue>
                          ) : (
                            <AdminDetailFieldValue as="span">—</AdminDetailFieldValue>
                          )}
                          {customer.mobile && customer.phoneVerified ? (
                            <i
                              className="fi fi-sr-check-circle text-freshgreen text-xs leading-none shrink-0"
                              title="Verified"
                              aria-label="Verified"
                            />
                          ) : null}
                          {customer.mobile ? (
                            <button
                              type="button"
                              onClick={() =>
                                handleCopyContactField(
                                  'mobile',
                                  String(customer.mobile).replace(/\D/g, '') || customer.mobile
                                )
                              }
                              className="inline-flex items-center justify-center shrink-0 text-mediumgray hover:text-primaryblue"
                              title={copiedContactField === 'mobile' ? 'Copied' : 'Copy mobile'}
                              aria-label={
                                copiedContactField === 'mobile' ? 'Copied' : 'Copy mobile'
                              }
                            >
                              <i
                                className={`fi ${
                                  copiedContactField === 'mobile'
                                    ? 'fi-rr-check text-freshgreen'
                                    : 'fi-rr-copy'
                                } text-xs leading-none`}
                                aria-hidden
                              />
                            </button>
                          ) : null}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Location</AdminDetailFieldLabel>
                        <AdminDetailFieldValue>{getOverviewLocation(customer)}</AdminDetailFieldValue>
                      </div>
                    </div>

                    {/* Column 2 — Activity */}
                    <div className="space-y-4">
                      {(() => {
                        const reviewsCount = getOverviewReviewsCount(customer);
                        const messagesCount = getOverviewMessagesCount(customer);
                        const quotesCount = getOverviewQuotesCount(customer);
                        const cid = overviewCustomerId;
                        return (
                          <>
                            {renderOverviewLinkedField({
                              label: 'Reviews',
                              valueText: String(reviewsCount),
                              enabled: reviewsCount > 0,
                              onNavigate: () =>
                                navigate('/admin/reviews/all', {
                                  state: { customerId: cid },
                                }),
                            })}
                            {renderOverviewLinkedField({
                              label: 'Messages',
                              valueText: String(messagesCount),
                              enabled: messagesCount > 0,
                              onNavigate: () =>
                                navigate('/admin/communication/all', {
                                  state: { customerId: cid },
                                }),
                            })}
                            {renderOverviewLinkedField({
                              label: 'Quotes',
                              valueText: String(quotesCount),
                              enabled: quotesCount > 0,
                              onNavigate: () =>
                                navigate('/admin/quotes/all', {
                                  state: { customerId: cid },
                                }),
                            })}
                          </>
                        );
                      })()}
                    </div>

                    {/* Column 3 — Account & Support */}
                    <div className="space-y-4">
                      {(() => {
                        const openFlags = getOpenFlagsOverviewCount(customer);
                        const supportTickets = getOverviewSupportTickets(customer);
                        const activeAppeals = getOverviewActiveAppeals(customer);
                        const cid = overviewCustomerId;
                        return (
                          <>
                            {renderOverviewLinkedField({
                              label: 'Open Flags',
                              valueText: String(openFlags),
                              enabled: true,
                              onNavigate: () => setActiveTab('flags'),
                            })}
                            {renderOverviewLinkedField({
                              label: 'Open Support Tickets',
                              valueText: String(supportTickets),
                              enabled: supportTickets > 0,
                              onNavigate: () =>
                                navigate('/admin/support/tickets', {
                                  state: { customerId: cid },
                                }),
                            })}
                            {renderOverviewLinkedField({
                              label: 'Active Appeals',
                              valueText: String(activeAppeals),
                              enabled: activeAppeals > 0,
                              onNavigate: () =>
                                navigate('/admin/appeals', {
                                  state: { customerId: cid },
                                }),
                            })}
                          </>
                        );
                      })()}
                    </div>

                    {/* Column 4 — Security Snapshot */}
                    <div className="space-y-4">
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Last Login</AdminDetailFieldLabel>
                        <AdminDetailFieldValue>
                          {formatDateTime(
                            customer.lastLogin ||
                              customer.lastActive ||
                              customer.devices?.[0]?.date
                          )}
                        </AdminDetailFieldValue>
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Active Sessions</AdminDetailFieldLabel>
                        <AdminDetailFieldValue className="tabular-nums text-offblack">
                          {customer.currentActiveSessions ?? customer.activeSessions ?? 0}
                        </AdminDetailFieldValue>
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>
                          Failed Login Attempts
                          <br />
                          Last 24 Hours
                        </AdminDetailFieldLabel>
                        <AdminDetailFieldValue className="tabular-nums text-offblack">
                          {customer.failedLoginAttempts24h ?? 0}
                        </AdminDetailFieldValue>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'flags' && (
              <div className="min-w-0 max-w-full">
                  <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
                    <div className="w-full min-w-0 max-w-full">
                      <div
                        className="grid min-w-0 py-3 text-offblack border-b border-lightgray gap-x-2 sm:gap-x-3 gap-y-2 px-4"
                        style={{ gridTemplateColumns: FLAGS_TABLE_GRID }}
                      >
                        <AdminDetailTableHeader className="min-w-0 break-words">Flag ID</AdminDetailTableHeader>
                        <AdminDetailTableHeader className="min-w-0 break-words">Case ID</AdminDetailTableHeader>
                        <AdminDetailTableHeader className="min-w-0 break-words">Source</AdminDetailTableHeader>
                        <AdminDetailTableHeader className="min-w-0 break-words">Reason</AdminDetailTableHeader>
                        <AdminDetailTableHeader className="min-w-0 break-words">Severity</AdminDetailTableHeader>
                        <AdminDetailTableHeader className="min-w-0 break-words">Case Status</AdminDetailTableHeader>
                        <AdminDetailTableHeader className="min-w-0 break-words leading-tight">Created On</AdminDetailTableHeader>
                        <AdminDetailTableHeader className="min-w-0 break-words leading-tight">Last Updated</AdminDetailTableHeader>
                        <AdminDetailTableHeader className="min-w-0 break-words">Action</AdminDetailTableHeader>
                      </div>

                      {paginatedFlagRows.length > 0 ? (
                        paginatedFlagRows.map((flag) => (
                          <div
                            key={flag.key}
                            className="grid min-w-0 py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-x-2 sm:gap-x-3 gap-y-2 items-start"
                            style={{ gridTemplateColumns: FLAGS_TABLE_GRID }}
                          >
                            <AdminTableBody className="min-w-0 break-words leading-snug">{flag.flagId}</AdminTableBody>
                            <AdminTableBody className="min-w-0 break-words break-all leading-snug">
                              {flag.handlingRecord || '—'}
                            </AdminTableBody>
                            <AdminTableBody className="min-w-0 break-words leading-snug">{flag.source}</AdminTableBody>
                            <AdminTableBody className="min-w-0 break-words break-all leading-snug" title={flag.reason}>
                              {flag.reason}
                            </AdminTableBody>
                            <AdminTableBody
                              emphasis
                              className={`min-w-0 break-words leading-snug ${getFlagSeverityTextClass(
                                flag.severity
                              )}`.trim()}
                            >
                              {flag.severity}
                            </AdminTableBody>
                            <div className="flex items-start min-w-0">
                              <AdminFlagStatusBadge status={flag.handlingStatus} />
                            </div>
                            <AdminTableBody className="min-w-0 break-words leading-snug">
                              {formatDateTime(flag.createdAt || flag.created)}
                            </AdminTableBody>
                            <AdminTableBody className="min-w-0 break-words leading-snug">
                              {formatDateTime(flag.updatedAt || flag.lastUpdated)}
                            </AdminTableBody>
                            <div className="flex items-start min-w-0">
                              {flag.actionLabel && flag.actionPath ? (
                                <button
                                  type="button"
                                  className="text-left bg-transparent border-0 p-0 cursor-pointer"
                                  onClick={() => handleFlagRowAction(flag)}
                                >
                                  <AdminTableBody className="text-offblack underline-offset-2 hover:underline">
                                    {flag.actionLabel}
                                  </AdminTableBody>
                                </button>
                              ) : (
                                <AdminTableBody>—</AdminTableBody>
                              )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="py-8 text-center">
                          <AdminSupportingText className="text-mediumgray">No flags found</AdminSupportingText>
                        </div>
                      )}
                    </div>
                  </DashboardMainContainer>
                  <ConfigurationListPaginationFooter
                    show={flagsTablePagination.showPagination}
                    rangeStart={flagsTablePagination.rangeStart}
                    rangeEnd={flagsTablePagination.rangeEnd}
                    totalCount={customerFlagRows.length}
                    currentPage={flagsTablePagination.effectivePage}
                    totalPages={flagsTablePagination.totalPages}
                    onPrev={() => setFlagsCurrentPage((prev) => Math.max(1, prev - 1))}
                    onNext={() =>
                      setFlagsCurrentPage((prev) =>
                        Math.min(flagsTablePagination.totalPages, prev + 1)
                      )
                    }
                    goToPage={setFlagsCurrentPage}
                  />
                </div>
            )}

            {activeTab === 'security' && (
              <div>
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                  {/* Column 1 — Login Activity */}
                  <div className="space-y-4">
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Last Login</AdminDetailFieldLabel>
                      <AdminDetailFieldValue>
                        {formatDateTime(
                          customer.lastLogin ||
                            customer.lastActive ||
                            customer.devices?.[0]?.date ||
                            customer.devices?.[0]?.lastActive
                        )}
                      </AdminDetailFieldValue>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>
                        Failed Login Attempts
                        <br />
                        Last 24 Hours
                      </AdminDetailFieldLabel>
                      <AdminDetailFieldValue className="tabular-nums text-offblack">
                        {customer.failedLoginAttempts24h ?? 0}
                      </AdminDetailFieldValue>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>
                        Failed Login Attempts
                        <br />
                        Last 7 Days
                      </AdminDetailFieldLabel>
                      <AdminDetailFieldValue className="tabular-nums text-offblack">
                        {customer.failedLoginAttempts7Days ?? 0}
                      </AdminDetailFieldValue>
                    </div>
                  </div>

                  {/* Column 2 — Sessions */}
                  <div className="space-y-4">
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Active Sessions</AdminDetailFieldLabel>
                      <AdminDetailFieldValue className="tabular-nums text-offblack">
                        {getSecurityActiveSessions(customer)}
                      </AdminDetailFieldValue>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Trusted Devices</AdminDetailFieldLabel>
                      <AdminDetailFieldValue className="tabular-nums text-offblack">
                        {customer.trustedDevices ?? (customer.devices || []).length ?? 0}
                      </AdminDetailFieldValue>
                    </div>
                  </div>

                  {/* Column 3 — Devices */}
                  <div className="space-y-4">
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Last Device Used</AdminDetailFieldLabel>
                      <AdminDetailFieldValue>
                        {customer.lastDeviceUsed || customer.devices?.[0]?.device || '—'}
                      </AdminDetailFieldValue>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Last Device Added</AdminDetailFieldLabel>
                      <AdminDetailFieldValue>
                        {customer.lastDeviceAdded
                          ? formatDate(customer.lastDeviceAdded)
                          : '—'}
                      </AdminDetailFieldValue>
                    </div>
                  </div>

                  {/* Column 4 — Security Actions */}
                  <div className="space-y-4">
                    <AdminHeaderAction
                      type="button"
                      disabled={getSecurityActiveSessions(customer) <= 0}
                      onClick={openForceLogoutConfirm}
                    >
                      Force Logout
                    </AdminHeaderAction>
                    <AdminHeaderAction type="button" onClick={openPasswordResetConfirm}>
                      Send Password Reset
                    </AdminHeaderAction>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'audit-log' && (
              <div className="min-w-0 max-w-full">
                <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
                  <div className="w-full min-w-0 max-w-full">
                    <div
                      className="grid min-w-0 py-3 text-offblack border-b border-lightgray gap-x-2 sm:gap-x-3 gap-y-2 px-4"
                      style={{ gridTemplateColumns: AUDIT_LOG_TABLE_GRID }}
                    >
                      <AdminDetailTableHeader className="min-w-0 break-words">
                        Date and Time
                      </AdminDetailTableHeader>
                      <AdminDetailTableHeader className="min-w-0 break-words">
                        Event
                      </AdminDetailTableHeader>
                      <AdminDetailTableHeader className="min-w-0 break-words">
                        Details
                      </AdminDetailTableHeader>
                      <AdminDetailTableHeader className="min-w-0 break-words">
                        Related Record
                      </AdminDetailTableHeader>
                    </div>

                    {(customer.auditLogs || []).length > 0 ? (
                      (customer.auditLogs || []).map((log, index) => {
                        const details =
                          log.details ||
                          [log.performedBy, log.summary].filter(Boolean).join(' · ');
                        const related =
                          log.relatedRecord ||
                          log.ref?.id ||
                          log.caseId ||
                          log.flagId ||
                          '—';
                        return (
                          <div
                            key={log.id || `audit-${index}`}
                            className="grid min-w-0 py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-x-2 sm:gap-x-3 gap-y-2 items-start"
                            style={{ gridTemplateColumns: AUDIT_LOG_TABLE_GRID }}
                          >
                            <AdminTableBody className="min-w-0 break-words leading-snug">
                              {log.dateTime || log.timestamp || '—'}
                            </AdminTableBody>
                            <AdminTableBody className="min-w-0 break-words leading-snug">
                              {log.action || log.event || '—'}
                            </AdminTableBody>
                            <AdminTableBody
                              className="min-w-0 break-words break-all leading-snug"
                              title={details || '—'}
                            >
                              {details || '—'}
                            </AdminTableBody>
                            <AdminTableBody className="min-w-0 break-words leading-snug">
                              {related}
                            </AdminTableBody>
                          </div>
                        );
                      })
                    ) : (
                      <div className="py-8 text-center">
                        <AdminSupportingText className="text-mediumgray">
                          No audit log entries
                        </AdminSupportingText>
                      </div>
                    )}
                  </div>
                </DashboardMainContainer>
              </div>
            )}
          </div>
        </DashboardMainContainer>
        </div>
      </div>
      {securityConfirm ? (
        <CustomerSecurityConfirmModal
          title={securityConfirm.title}
          message={securityConfirm.message}
          confirmLabel={securityConfirm.confirmLabel}
          onConfirm={handleSecurityConfirm}
          onClose={() => setSecurityConfirm(null)}
        />
      ) : null}
    </AdminLayout>
  );
};

export default CustomerDetail;

